import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import ContactCTA from "@/components/ContactCTA";
import {
    getProjectBySlug,
    getAdjacentProjects,
    getRelatedProjects,
} from "@/lib/projects";

const ease = [0.22, 1, 0.36, 1];

export default function CaseStudy() {
    const { slug } = useParams();
    const project = getProjectBySlug(slug);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [slug]);

    if (!project) return <Navigate to="/work" replace />;

    const { prev, next } = getAdjacentProjects(slug);
    const related = getRelatedProjects(slug, 3);

    return (
        <>
            <Seo
                title={`${project.title} — ${project.result} | DigitalBeej`}
                description={project.blurb}
                path={`/work/${project.id}`}
            />

            {/* Hero */}
            <section
                data-testid="case-study-hero"
                className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease }}
                        className="flex flex-wrap items-center justify-between gap-4 mb-10 text-xs uppercase tracking-[0.22em] font-bold text-black/55"
                    >
                        <Link
                            to="/work"
                            data-testid="case-study-back-link"
                            className="link-underline"
                        >
                            ← All case studies
                        </Link>
                        <span>{project.industry} · {project.year}</span>
                    </motion.div>

                    <div className="grid grid-cols-12 gap-y-8 lg:gap-x-10 items-end">
                        <div className="col-span-12 lg:col-span-8">
                            {project.logo && (
                                <motion.img
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.7, ease }}
                                    src={project.logo}
                                    alt=""
                                    aria-hidden
                                    className="h-9 w-auto object-contain mb-6"
                                />
                            )}
                            <motion.h1
                                data-testid="case-study-title"
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease }}
                                className="font-display font-black uppercase leading-[0.92] tracking-[-0.03em] text-5xl sm:text-6xl lg:text-[6.5vw]"
                            >
                                {project.title}
                            </motion.h1>
                        </div>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease, delay: 0.2 }}
                            className="col-span-12 lg:col-span-4 font-serif italic text-xl lg:text-2xl text-black/65 lg:pb-2"
                        >
                            {project.blurb}
                        </motion.p>
                    </div>
                </div>
            </section>

            {/* Banner image */}
            <section className="relative">
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <motion.div
                        initial={{ opacity: 0, scale: 1.02 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, ease }}
                        data-testid="case-study-banner"
                        className="relative overflow-hidden aspect-[16/9] bg-black/5"
                    >
                        <img
                            src={project.img}
                            alt={project.title}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="eager"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Results breakdown */}
            <section
                data-testid="case-study-results"
                className="relative py-16 lg:py-24"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <div className="grid grid-cols-12 gap-y-10 lg:gap-x-10 border-t border-black/15 pt-12">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mb-4">
                                Headline result
                            </div>
                            <div
                                data-testid="case-study-result-metric"
                                className="font-display font-black text-[#2E5019] text-4xl lg:text-5xl leading-tight"
                            >
                                {project.result}
                            </div>
                            <div className="mt-3 text-sm text-black/60">
                                Delivered in {project.window}
                            </div>
                        </div>

                        <div className="col-span-12 lg:col-span-7">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mb-4">
                                Capabilities delivered
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {project.tags.map((t) => (
                                    <span
                                        key={t}
                                        className="text-xs uppercase tracking-[0.16em] font-bold border border-black/20 px-3 py-2"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                            <p className="mt-6 text-black/70 leading-relaxed max-w-xl">
                                {project.blurb}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Full case study visual */}
            {project.caseStudy && (
                <section
                    data-testid="case-study-full"
                    className="relative pb-16 lg:pb-24"
                >
                    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mb-6">
                            / The full story
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease }}
                            className="relative overflow-hidden bg-black/[0.03]"
                        >
                            <img
                                src={project.caseStudy}
                                alt={`${project.title} — full case study`}
                                className="w-full h-auto object-contain"
                                loading="lazy"
                            />
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Prev / Next navigation */}
            <section
                data-testid="case-study-nav"
                className="border-t border-black/15"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-2">
                    <Link
                        to={`/work/${prev.id}`}
                        data-testid="case-study-prev-link"
                        className="group py-8 sm:pr-8 sm:border-r border-black/15 flex items-center gap-4 hover:bg-black/[0.02] transition-colors duration-300"
                    >
                        <span className="transition-transform duration-300 group-hover:-translate-x-1">
                            ←
                        </span>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50">
                                Previous
                            </div>
                            <div className="mt-1 font-display font-bold text-lg">
                                {prev.title}
                            </div>
                        </div>
                    </Link>
                    <Link
                        to={`/work/${next.id}`}
                        data-testid="case-study-next-link"
                        className="group py-8 sm:pl-8 flex items-center justify-between gap-4 hover:bg-black/[0.02] transition-colors duration-300"
                    >
                        <div className="text-right w-full sm:w-auto">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50">
                                Next
                            </div>
                            <div className="mt-1 font-display font-bold text-lg">
                                {next.title}
                            </div>
                        </div>
                        <span className="transition-transform duration-300 group-hover:translate-x-1">
                            →
                        </span>
                    </Link>
                </div>
            </section>

            {/* Related case studies */}
            {related.length > 0 && (
                <section
                    data-testid="case-study-related"
                    className="relative py-16 lg:py-24 border-t border-black/15"
                >
                    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                            <h2 className="font-display font-black uppercase tracking-tight text-3xl lg:text-4xl">
                                Keep exploring
                            </h2>
                            <p className="font-serif italic text-lg text-black/60">
                                More brands, more stories.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
                            {related.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{
                                        duration: 0.7,
                                        ease,
                                        delay: i * 0.08,
                                    }}
                                >
                                    <Link
                                        to={`/work/${p.id}`}
                                        data-testid={`case-study-related-${p.id}`}
                                        className="group block"
                                    >
                                        <div className="relative overflow-hidden aspect-[4/3] bg-black/[0.04]">
                                            <img
                                                src={p.img}
                                                alt={p.title}
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                                                loading="lazy"
                                            />
                                            <div className="absolute bottom-4 right-4 text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                View case study →
                                            </div>
                                        </div>
                                        <div className="pt-4">
                                            <h3 className="font-display font-bold tracking-tight text-xl group-hover:text-olive transition-colors duration-300">
                                                {p.title}
                                            </h3>
                                            <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-bold text-black/50">
                                                {p.industry}
                                            </div>
                                            <div className="mt-2 text-xs uppercase tracking-[0.18em] font-bold text-[#2E5019]">
                                                {p.result}
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <ContactCTA
                kicker="Like what you see?"
                title="Let's write yours."
                body="If your brand feels ready to grow — or stuck in a plateau — we'd love to hear where you are."
            />
        </>
    );
}
